import { isOutgoingHandoffJobForPartner } from './handoffWorkflowUtils';

export const EMPTY_QUEUE_FILTERS = {
  people: [],
  deviceCategory: [],
  priority: [],
  warrantyOnly: false,
  inServiceOnly: false,
  serviceLocation: [],
  returnVisitOnly: false,
  unassignedOnly: false
};

export const hasActiveQueueFilters = (filters = {}) =>
  (Array.isArray(filters.people) && filters.people.length > 0) ||
  (Array.isArray(filters.deviceCategory) && filters.deviceCategory.length > 0) ||
  (Array.isArray(filters.priority) && filters.priority.length > 0) ||
  !!filters.warrantyOnly ||
  !!filters.inServiceOnly ||
  (Array.isArray(filters.serviceLocation) && filters.serviceLocation.length > 0) ||
  !!filters.returnVisitOnly ||
  !!filters.unassignedOnly;

const getJobSearchText = (job) => {
  const addressString = job?.clientAddress
    ? typeof job.clientAddress === 'string'
      ? job.clientAddress
      : [
          job.clientAddress.street,
          job.clientAddress.number,
          job.clientAddress.floor,
          job.clientAddress.apartment
        ]
          .filter(Boolean)
          .join(' ')
    : '';

  return [
    job?.clientName,
    addressString,
    job?.deviceModel,
    job?.deviceBrand,
    job?.deviceType,
    job?.deviceSerialNumber,
    job?.clientEmail,
    job?.clientPhone,
    job?.issueDescription,
    job?.jobNumber,
    job?.report?.workDescription,
    job?.report?.additionalNotes,
    job?.report?.fiscalReceiptNumber
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export const jobMatchesSearch = (job, searchTerm) => {
  const term = String(searchTerm || '').trim().toLowerCase();
  if (!term) return true;
  return getJobSearchText(job).includes(term);
};

export const isJobUnassigned = (job) => {
  const assigned = job?.assignedTo;
  if (assigned == null || assigned === 'Unassigned' || assigned === '') return true;
  if (typeof assigned === 'string' && assigned.trim() === '') return true;
  return false;
};

export const jobHasWarranty = (job) =>
  job?.hasWarranty === true ||
  job?.warranty === true ||
  job?.warranty === 'yes' ||
  job?.report?.underWarranty === true;

export const jobMatchesWorkerSelection = (job, workerIds = [], workers = []) => {
  if (!workerIds.length) return true;
  if (isJobUnassigned(job)) return false;

  return workerIds.some((filterWorkerId) => {
    const workerId = String(filterWorkerId).trim();
    if (!workerId) return false;

    if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
      const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
      if (assignedToId && assignedToId === workerId) return true;
    }

    const assignedToStr = String(job.assignedTo || '').trim();
    if (!assignedToStr || assignedToStr === 'Unassigned') return false;
    if (assignedToStr === workerId) return true;
    if (assignedToStr.startsWith('worker:') && assignedToStr.replace('worker:', '').trim() === workerId) {
      return true;
    }

    const selectedWorker = workers.find((w) => String(w._id || w.id || '').trim() === workerId);
    if (selectedWorker) {
      const workerName = `${selectedWorker.firstName || ''} ${selectedWorker.lastName || ''}`.trim();
      if (
        assignedToStr === workerName ||
        assignedToStr === selectedWorker.firstName ||
        assignedToStr === selectedWorker.lastName
      ) {
        return true;
      }
    }

    return false;
  });
};

export const jobMatchesPartnerSelection = (job, partnerIds = [], partners = [], currentCompanyId) => {
  if (!partnerIds.length) return true;
  return partnerIds.some((pid) => {
    const partner = (partners || []).find((p) => String(p._id || p.id) === String(pid));
    return partner && isOutgoingHandoffJobForPartner(job, partner, currentCompanyId);
  });
};

export const jobMatchesDeviceCategories = (job, categoryIds = [], deviceCategories = []) => {
  if (!categoryIds.length) return true;
  return categoryIds.some((filterCategoryId) => {
    if (job.deviceCategoryId && String(job.deviceCategoryId) === String(filterCategoryId)) return true;
    const selectedCategory = deviceCategories.find(
      (cat) => String(cat.id || cat._id) === String(filterCategoryId)
    );
    if (selectedCategory && (job.deviceCategoryName === selectedCategory.name || job.deviceType === selectedCategory.name)) {
      return true;
    }
    return false;
  });
};

export const jobMatchesPriorities = (job, priorities = []) => {
  if (!priorities.length) return true;
  return priorities.includes(job.priority);
};

export const jobMatchesQueueCriteria = (job, filters = {}, options = {}) => {
  const people = Array.isArray(filters.people) ? filters.people : [];
  const categories = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : [];
  const priorities = Array.isArray(filters.priority) ? filters.priority : [];
  const locations = Array.isArray(filters.serviceLocation) ? filters.serviceLocation : [];

  if (options.mode === 'partner') {
    if (!jobMatchesPartnerSelection(job, people, options.partners, options.currentCompanyId)) return false;
  } else {
    if (filters.unassignedOnly && !isJobUnassigned(job)) return false;
    if (!filters.unassignedOnly && !jobMatchesWorkerSelection(job, people, options.workers)) return false;
  }

  if (!jobMatchesDeviceCategories(job, categories, options.deviceCategories)) return false;
  if (!jobMatchesPriorities(job, priorities)) return false;
  if (filters.warrantyOnly && !jobHasWarranty(job)) return false;

  if (filters.inServiceOnly) {
    const inService =
      job.serviceLocation === 'InWorkshop' ||
      ['In Repair', 'In Progress', 'Diagnosing', 'Waiting for Parts'].includes(job.status);
    if (!inService) return false;
  }

  if (locations.length > 0) {
    const jobLoc = job.serviceLocation === 'InWorkshop' ? 'InWorkshop' : 'OnSite';
    if (!locations.includes(jobLoc)) return false;
  }

  if (filters.returnVisitOnly && !(job.isReturnVisit || job.lastVisitDate)) return false;
  return true;
};
